import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import { getQuarter, getYear } from 'date-fns';
import type { RootState } from '~/types/store/index';
import type { MyPortfolioDealsFormState } from '~/types/store/my-portfolio-deals-form';
import type {
  PortfolioCurrentUser,
  PortfolioDeal,
  PortfolioDealQuarter,
  Mutation,
  MutationCreateDealsArgs,
} from '~/types/graphql';
import CreateDealsMutation from '~/apollo/mutations/CreateDeals.mutation';
import { uid } from '~/components/utils/core/uid';
import { PortfolioCurrentUserDealsQuery } from '~/apollo/queries/PortfolioCurrentUserDeals.query';
import { pick } from '~/components/utils/core/object';
import type { FormState } from '~/types/common';
import { DEAL_SOURCE_PERFORMANCE_INDEX } from '~/constants/deals';

function getEmptyDeal(): Partial<PortfolioDeal> {
  return {
    id: uid(),
    amount: 0,
    closeDate: '',
    company: { id: '-1', name: '' },
    departments: [],
    isPrivateCompany: true,
    isPrivateDepartments: true,
  };
}

const getInitialState = (): MyPortfolioDealsFormState => ({
  deals: [getEmptyDeal()],
  formState: 'idle',
});

export const state = getInitialState;

export const getters: GetterTree<MyPortfolioDealsFormState, RootState> = {
  lastDeal(state) {
    return state.deals[state.deals.length - 1];
  },
  filledDeals(state) {
    return state.deals.filter(
      deal => deal.amount && deal.closeDate && deal.company?.id !== '-1' && deal.departments?.length,
    );
  },
  totalRevenue(state) {
    return state.deals.reduce((revenue, deal) => (deal.amount ? revenue + deal.amount : revenue), 0);
  },
};

export const mutations: MutationTree<MyPortfolioDealsFormState> = {
  resetState(state) {
    Object.assign(state, getInitialState());
  },
  addEmptyDeal(state) {
    state.deals.push(getEmptyDeal());
    state.formState = 'idle';
  },
  updateDeal(state, { index, ...updates }) {
    const deal = state.deals[index];

    if (deal) {
      Object.assign(deal, updates);
    }
  },
  removeDeal(state, { index }) {
    if (state.deals.length > 1) {
      state.deals.splice(index, 1);
    }
  },
  removeDealById(state, { id }: { id: string }) {
    const dealIndex = state.deals.findIndex(deal => deal.id === id);

    if (dealIndex > -1) {
      state.deals.splice(dealIndex, 1);
    }
  },
  changeFormState(state, { formState }: { formState: FormState }) {
    state.formState = formState;
  },
};

export const actions: ActionTree<MyPortfolioDealsFormState, RootState> = {
  async createDeals({ getters }) {
    if (this.app.apolloProvider) {
      const createDealsInput = (getters.filledDeals as PortfolioDeal[]).map(deal => ({
        amount: deal.amount,
        closeDate: deal.closeDate,
        company: {
          id: deal.company!.id,
          name: deal.company?.name,
        },
        source: DEAL_SOURCE_PERFORMANCE_INDEX,
        departments: deal.departments?.map(d => pick(d, ['id', 'name'])) || [],
        isPrivateCompany: deal.isPrivateCompany,
        isPrivateDepartments: deal.isPrivateDepartments,
      }));

      try {
        await this.app.apolloProvider.defaultClient.mutate<
          { createDeals: Mutation['createDeals'] },
          MutationCreateDealsArgs
        >({
          mutation: CreateDealsMutation,
          variables: {
            input: {
              deals: createDealsInput,
            },
          },
          update(store, result) {
            const data = store.readQuery<{ portfolioCurrentUser: PortfolioCurrentUser }>({
              query: PortfolioCurrentUserDealsQuery,
            });

            if (data && result.data?.createDeals?.length) {
              const existingQuarters = data.portfolioCurrentUser.dealQuarters.reduce<Record<string, boolean>>(
                (hash, q) => {
                  hash[`${q.year}-${q.quarter}`] = true;

                  return hash;
                },
                {},
              );
              const newQuarters = result.data.createDeals.reduce<Record<string, PortfolioDealQuarter>>((hash, deal) => {
                const dealClosedDate = new Date(deal.closeDate);
                const year = getYear(dealClosedDate);
                const quarter = getQuarter(dealClosedDate);
                const key = `${year}-${quarter}`;

                if (!existingQuarters[key] && !hash[key]) {
                  hash[`${year}-${quarter}`] = {
                    __typename: 'PortfolioDealQuarter',
                    quota: null,
                    revenue: null,
                    year,
                    quarter,
                  };
                }

                return hash;
              }, {});

              const newDealQuartersList = Object.values(newQuarters);

              if (newDealQuartersList.length) {
                data.portfolioCurrentUser.dealQuarters.push(...newDealQuartersList);
              }

              data.portfolioCurrentUser.deals.nodes.push(...result.data.createDeals);

              store.writeQuery({ query: PortfolioCurrentUserDealsQuery, data });
            }
          },
        });
      } catch (e) {
        // @ts-ignore
        throw new Error(e);
      }
    }
  },
};
